import React, { useCallback, useContext, useEffect } from 'react';
import { CAContext } from '../../contexts/CAContext';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { util } from '../../utils/miaw/common';
import { DISPLAY_NAME } from '../../config/app-config';
import ButtonGroup from '../AdvanceMessagingComponent/ButtonGroup/ButtonGroup';
import PrechatForm from './PrechatFormInline';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { useIdentity } from '@vp/auth-react';
import { IIdentityObject } from '../../interfaces/app-interfaces';
import { useNRLogging } from '../../hooks/custom-hooks/useNRLogging';

function SummaryFlowLogic({ formData, setAutoFilled, setFormData, correctSummary }) {
  const identity = useIdentity();
  const { logCustomAttributesNR } = useNRLogging();
  const {
    chatState: { userInfo, chatbotSummaryResponse },
    setChatState,
  } = useContext(CAContext);

  const {
    state: { startMiawChatSession },
    setState: setMiawChatState,
  } = useContext(MiawChatContext);

  const {
    state: { sessionId, orderNumber: CaOrderNumber },
    setState: setChatbotState,
  } = useContext(ChatbotContext);

  const handleSummaryGenerated = () => {
    const summaryMessageId = util.generateUUID();
    const timestamp = Date.now();
    setChatbotState((prevState) => ({
      ...prevState,
      isSendMessageLoading: true,
      conversationMessages: [
        ...prevState.conversationMessages,
        {
          id: summaryMessageId,
          timestamp,
          type: 'chatbot',
          messageContent: `Last thing! Please review the summary below. I’ll pass it to the specialist so you won’t need to repeat yourself:\n\n
${formData.chatbotContactReason}`,
          additionalComponents: (
            <ButtonGroup
              type="buttons"
              actionList={[
                { label: 'Yes', value: 'Yes', isPreChatFlowButton: true },
                { label: 'No', value: 'No', isPreChatFlowButton: true },
              ]}
              onSelect={(value: string) => handleOnSelectSummaryCheck(value, summaryMessageId)}
            />
          ),
          isMessageRead: false,
          showFeedback: false,
        },
      ],
    }));
  };

  function handleOnSelectSummaryCheck(value: string, summaryMessageId: string) {
    if (value === 'No') {
      logCustomAttributesNR('ca-chatbot-info', {
        logName: 'escalation_flow_user_clicked_no',
        logLevel: 'info',
        message: 'User clicked on No button and edited the summary',
      });
      setChatbotState((prev) => ({
        ...prev,
        isSendMessageLoading: true,
        conversationMessages: removeButtons(prev.conversationMessages, summaryMessageId),
      }));

      // Remove buttons from summary message
      setChatbotState((prev) => ({
        ...prev,
        isSendMessageLoading: true,
        conversationMessages: removeButtons(prev.conversationMessages, summaryMessageId),
      }));

      // set No as user message
      setChatbotState((prevState) => ({
        ...prevState,
        isSendMessageLoading: true,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: 'No',
            timestamp: new Date().getTime(),
            type: 'user',
            isMessageRead: false,
            showFeedback: false,
          },
        ],
      }));

      // set chatbot message to ask for correction
      setChatbotState((prevState) => ({
        ...prevState,
        isSendMessageLoading: true,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: 'Can you please type in the correction needed?',
            timestamp: new Date().getTime(),
            type: 'chatbot',
            isMessageRead: false,
            senderDisplayName: DISPLAY_NAME.CHATBOT,
            additionalComponents: <PrechatForm onSubmit={handleSummaryCorrectedByUser} correctSummary={true} />,
            customMessageType: 'prechatMessage',
            showFeedback: false,
          },
        ],
      }));
    } else {
      logCustomAttributesNR('ca-chatbot-info', {
        logName: 'escalation_flow_user_clicked_yes',
        logLevel: 'info',
        message: 'User clicked on Yes button and accepted the generated response',
      });

      setChatbotState((prev) => ({
        ...prev,
        isSendMessageLoading: true,
        conversationMessages: removeButtons(prev.conversationMessages, summaryMessageId),
      }));

      setMiawChatState((prevState) => ({
        ...prevState,
        isSendMessageLoading: true,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: 'Yes',
            timestamp: new Date().getTime(),
            type: 'user',
            isMessageRead: false,
            showFeedback: false,
          },
        ],
      }));
      startMiawChatSession?.({
        email: userInfo?.isLoggedIn && userInfo?.userData ? userInfo?.userData.email : formData.email || '',
        firstName: userInfo?.isLoggedIn && userInfo?.userData ? userInfo?.userData.firstName : formData.firstName || '',
        lastName: userInfo?.isLoggedIn && userInfo?.userData ? userInfo?.userData.lastName : formData.lastName || '',
        chatbotIdentifier: sessionId || '',
        chatbotContactReason: formData.chatbotContactReason || '',
        orderNumber:
          userInfo?.isLoggedIn && userInfo?.userData
            ? userInfo?.userData?.orderNumber
              ? userInfo?.userData?.orderNumber
              : formData.orderNumber
            : formData.orderNumber,
      });
    }
  }

  const handleSummaryCorrectedByUser = useCallback(
    (data: { firstName: string; lastName: string; email: string; chatbotContactReason: string; orderNumber: string }) => {
      setChatState((prevState) => ({
        ...prevState,
        userInfo: {
          isLoggedIn: prevState.userInfo?.isLoggedIn ?? false,
          userData: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            shopperId: (identity as IIdentityObject).canonicalId || '',
            chatbotContactReason: data?.chatbotContactReason || '',
            orderNumber: data?.orderNumber || '',
          },
        },
      }));
      setChatbotState((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: data.chatbotContactReason,
            timestamp: new Date().getTime(),
            type: 'user',
            isMessageRead: false,
            showFeedback: false,
          },
        ],
      }));
      startMiawChatSession?.({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        chatbotIdentifier: sessionId || '',
        chatbotContactReason: data?.chatbotContactReason || '',
      });

      // Remove Prechat Form from the conversation
      setChatbotState((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => {
          if (message.customMessageType === 'prechatMessage') {
            return { ...message, additionalComponents: undefined };
          }
          return message;
        }),
      }));
    },
    [sessionId, startMiawChatSession, setChatState, setChatbotState, identity],
  );

  const removeButtons = (messages, summaryMessageId) =>
    messages.map((msg) => (msg.id === summaryMessageId ? { ...msg, additionalComponents: undefined } : msg));

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = chatbotSummaryResponse;
        const inquiry = response?.summary == 'NA' || response?.summary == '' ? '' : response?.summary;
        const isValidInquiry = !!inquiry;

        setAutoFilled(isValidInquiry);
        setFormData((prev) => ({
          ...prev,
          isAPICalled: true,
          chatbotContactReason: isValidInquiry ? inquiry : '',
          orderNumber: response?.order_id === '' || response?.order_id === 'NA' ? '' : response?.order_id,
        }));
        if (!CaOrderNumber) {
          setChatbotState((prev) => ({
            ...prev,
            orderNumber: response?.order_id === '' || response?.order_id === 'NA' ? '' : response?.order_id,
          }));
        }
      } catch {
        setFormData((prev) => ({ ...prev, isAPICalled: true }));
        setChatbotState((prev) => ({
          ...prev,
          isSendMessageLoading: false,
        }));
      }
    };

    if (userInfo && userInfo.userData && correctSummary) {
      const chatbotContactReasonString = `${userInfo?.userData?.chatbotContactReason}`;

      setFormData((prev) => ({
        ...prev,
        isAPICalled: true,
        firstName: userInfo?.userData?.firstName,
        lastName: userInfo?.userData?.lastName,
        email: userInfo?.userData?.email,
        chatbotContactReason: chatbotContactReasonString,
      }));
    } else {
      fetchSummary();
    }
  }, []);
  return {
    handleSummaryGenerated,
  };
}

export default SummaryFlowLogic;
